import React from 'react';
import { is, path as rPath } from 'ramda';
import PropTypes from 'prop-types';

import ProductCard from '../ProductCard/ProductCard';
import { PrimaryButton } from '@huggg/react-components';
import styles from './segmentCarouselDisplay.module.scss';

/**
 * Performs an ordered search through a list of paths
 * and returns the value of the first found.
 */
const firstPath = (paths, search) => {
  const path = paths.find(path => rPath(path, search));

  return path ? rPath(path, search) : null;
};

/**
 * A button to handle the click in an elegant way. If you create an anonymous
 * function to handle click events, React re-evaluates this every render, which
 * can get expensive. This is a utility component to fix that.
 *
 * It is exported for testing, but should not be used externally.
 *
 * @param {Object} props
 */
const SegmentCardDisplay = props => {
  const { shadow, segment, huggg, chooseSegment } = props;

  const { enabled: isOOS = false, message: messageOOS = '' } =
    segment.configuration?.outOfStock ?? {};

  // Handle missing huggg gracefully
  const searchContext = huggg ? { segment, huggg } : { segment };

  const images = firstPath(
    [
      ['segment', 'images'],
      // Only add huggg paths if huggg exists
      ...(huggg
        ? [
            ['huggg', 'purchase', 'claim_image_url'],
            ['huggg', 'purchase', 'image_url']
          ]
        : [])
    ],
    searchContext
  );

  const imageUrls = is(String, images)
    ? [images]
    : is(Array, images)
    ? images
    : [];

  return (
    <ProductCard
      title={segment.name}
      description={segment.description}
      imageUrls={imageUrls}
      shadow={shadow}
      hidePoweredBy={true}
      inSlider={true}
      isOOS={isOOS}
      messageOOS={messageOOS}
    >
      {/* Only render button if chooseSegment function exists */}
      {chooseSegment && (
        <div>
          <PrimaryButton
            disabled={isOOS}
            onClick={chooseSegment}
            data-test-id="confirm-segments-button"
            className={styles['confirm-button']}
            label="CHOOSE THIS ONE"
          />
        </div>
      )}
    </ProductCard>
  );
};

SegmentCardDisplay.propTypes = {
  segment: PropTypes.object.isRequired,
  huggg: PropTypes.object,
  shadow: PropTypes.bool,
  chooseSegment: PropTypes.func
};

export default SegmentCardDisplay;
